import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';

import { Formik } from 'formik';
import * as Yup from 'yup';
import 'yup-phone';

import CalendarDatePicker from '../components/form/CalendarDatePicker';
import Select from '../components/form/Select';
import TableSelectForm from '../components/form/TableSelectForm';
import { retrieve_schedule } from '../api/schedule';
import { retrieve_reservation_setting } from '../api/reservation_setting';
import { type ReservationProps } from '../types/ScreenTypes';
import { type ReservationInfoType } from '../types/Reservation';
import {
    type TableType,
    type AvailableMapType,
    type ScheduleType,
} from '../types/Schema';
import {
    type RetrieveReservationSettingType,
    type RetrieveScheduleType,
} from '../types/API';
import { ScreenSizeContext } from '../context/ScreenSizeContext';
import Input from '../components/form/Input';
import TelInput from '../components/form/TelInput';
import CheckBoxItemDictForm from '../components/form/CheckBoxItemDictForm';

import FormStyle from '../styles/Form';
import { type ReservationSettingTypeType } from 'types/ReservationSetting';
import { useTranslation } from 'react-i18next';
import { Button, useTheme } from '@rneui/themed';

import {
    nameMaxLength,
    descriptionMaxLength,
    phoneNumberMinLength,
    phoneNumberMaxLength,
} from '../const/textLength';
import PaymentForm from '../components/form/PaymentForm';

import { APP_STRIPE_PUBLIC_KEY } from '@env';

const styles = StyleSheet.create({
    container: {
        width: '100%',
        padding: 24,
        backgroundColor: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
    },
});

const Reservation: React.FC<ReservationProps> = ({ navigation, route }) => {
    const { t } = useTranslation();
    const { theme } = useTheme();

    const baseSchema = {
        date: Yup.date()
            .nullable()
            .required(t('date') + ' ' + t('required')),
        time: Yup.string()
            .nullable()
            .required(t('time') + ' ' + t('required')),
        people_num: Yup.string()
            .nullable()
            .required(t('peopleNumber') + ' ' + t('required')),
        table: Yup.mixed()
            .nullable()
            .required(t('table') + ' ' + t('required')),
        name: Yup.string()
            .required(t('name') + ' ' + t('required'))
            .max(nameMaxLength, t('name') + ' ' + t('tooLong')),
        email: Yup.string()
            .email(t('email') + ' ' + t('invalid'))
            .required(t('email') + ' ' + t('required'))
            .max(
                descriptionMaxLength,
                t('email') + ' ' + descriptionMaxLength + t('tooLong')
            ),
        email_confirm: Yup.string()
            .max(
                descriptionMaxLength,
                t('emailConfirmation') +
                    ' ' +
                    descriptionMaxLength +
                    t('tooLong')
            )
            .oneOf(
                [Yup.ref('email'), null],
                t('emailConfirmation') + ' ' + t('match')
            )
            .required(t('emailConfirmation') + ' ' + t('required'))
            .max(
                descriptionMaxLength,
                t('emailConfirmation') +
                    ' ' +
                    descriptionMaxLength +
                    t('tooLong')
            ),
        tel: Yup.string()
            .required(t('phoneNumber') + ' ' + t('required'))
            // .min(
            //     phoneNumberMinLength,
            //     t('phoneNumber') + ' ' + phoneNumberMinLength + t('tooShort')
            // )
            .max(
                phoneNumberMaxLength,
                t('phoneNumber') + ' ' + phoneNumberMinLength + t('tooLong')
            ),
        accommodation: Yup.string().max(
            nameMaxLength,
            t('accommodation') + ' ' + nameMaxLength + t('tooLong')
        ),
        notes: Yup.string().max(
            descriptionMaxLength,
            t('notes') + ' ' + descriptionMaxLength + t('tooLong')
        ),
    };

    const baseInitialValues = {
        date: '',
        time: '',
        table: null,
        people_num: 0,
        name: '',
        email: '',
        email_confirm: '',
        tel: '',
        options: [],
        accommodation: '',
        notes: '',
    };

    if (APP_STRIPE_PUBLIC_KEY && APP_STRIPE_PUBLIC_KEY !== '') {
        baseSchema.payment = Yup.object().required(
            t('payment') + ' ' + t('required')
        );
        baseInitialValues.payment = null;
    }

    const schema = Yup.object().shape(baseSchema);
    const initialValues = baseInitialValues;

    const [schedule, setSchedule] = React.useState<ScheduleType[]>([]);
    const dimensions = React.useContext(ScreenSizeContext);

    const [reservationSetting, setReservationSetting] =
        React.useState<ReservationSettingTypeType>({});

    const onSubmit = (values: ReservationInfoType, actions: any): void => {
        console.log(values);
        const submitValues = {
            ...values,
            actions,
        };
        navigation.navigate('ReservationConfirm', submitValues);
    };

    const updateTimeForDate = (
        schedule: ScheduleType[],
        date: string
    ): string[] => {
        if (schedule === null || schedule === undefined) return [];
        const availableMap: AvailableMapType | undefined = schedule?.find(
            (item: ScheduleType) => item.date === date
        )?.available_map;
        if (availableMap === null || availableMap === undefined) return [];
        return Object.keys(availableMap).sort((a, b) => (a > b ? 1 : -1));
    };

    const updatePeopleForTime = (
        schedule: ScheduleType[],
        date: string,
        time: string
    ): number[] => {
        const availableMap = schedule?.find(
            (item: ScheduleType) => item.date === date
        )?.available_map;
        if (availableMap === null || availableMap === undefined) return [];
        const tableTime = Object.keys(availableMap).find(
            (item) => item === time
        );
        if (tableTime !== null && tableTime !== undefined) {
            const timeMap = availableMap[tableTime];
            const tableList = timeMap?.tables;
            const maxCapacity = tableList.reduce(
                (max: number, item: any) =>
                    item.capacity > max ? item.capacity : max,
                tableList[0].capacity
            );
            return Array.from({ length: maxCapacity }, (_, i) => i + 1);
        } else return [];
    };

    const updateTableForPeople = (
        schedule: ScheduleType[],
        date: string,
        time: string,
        people: number
    ): TableType[] => {
        const availableMap = schedule?.find(
            (item: ScheduleType) => item.date === date
        )?.available_map;
        if (availableMap === null || availableMap === undefined) return [];
        const tableTime = Object.keys(availableMap).find(
            (item: any) => item === time
        );
        if (tableTime !== null && tableTime !== undefined) {
            const timeMap = availableMap[tableTime];
            const tableList = timeMap?.tables;
            if (tableList !== null && tableList !== undefined) {
                const selectableTableList = tableList.filter(
                    (item: any) => item.capacity >= people
                );
                return selectableTableList;
            }
            return [];
        } else return [];
    };

    const updateTablMapForPeople = (
        schedule: ScheduleType[],
        date: string,
        time: string,
        people: number
    ): TableType[] => {
        const availableMap = schedule?.find(
            (item: ScheduleType) => item.date === date
        )?.available_map;
        if (availableMap === null || availableMap === undefined) return [];
        const tableTime = Object.keys(availableMap).find(
            (item: any) => item === time
        );
        if (tableTime !== null && tableTime !== undefined) {
            const timeMap = availableMap[tableTime];
            const tableMapList = timeMap?.table_map;
            if (
                tableMapList !== null &&
                tableMapList !== undefined &&
                tableMapList.length > 0
            ) {
                const selectableTableMapList = tableMapList.map((item: any) => {
                    const tableMap = {
                        ...item,
                        table_list: [
                            ...item.table_list.filter(
                                (table: any) => table.capacity >= people
                            ),
                        ],
                    };
                    return tableMap;
                });
                return selectableTableMapList;
            }
            return [];
        } else return [];
    };

    const afterRetrieveSchedule = (data: RetrieveScheduleType): void => {
        const scheduleList = data?.items;
        if (scheduleList !== null && scheduleList !== undefined)
            setSchedule(scheduleList);
    };

    const afterRetrieveReservationSetting = (
        data: RetrieveReservationSettingType
    ): void => {
        const resSetting = data.item;
        setReservationSetting(resSetting);
    };

    React.useEffect(() => {
        void retrieve_reservation_setting(afterRetrieveReservationSetting);
        void retrieve_schedule(afterRetrieveSchedule);
    }, []);

    return (
        <ScrollView contentContainerStyle={styles.container}>
            <View
                style={{
                    width: dimensions.window.width > 600 ? '50%' : '80%',
                    alignContent: 'center',
                }}
            >
                <Formik
                    initialValues={initialValues}
                    validationSchema={schema}
                    onSubmit={onSubmit}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        isValid,
                        isSubmitting,
                        values,
                        errors,
                        touched,
                        setFieldValue,
                    }) => {
                        return (
                            <View>
                                <CalendarDatePicker
                                    label={t('date')}
                                    title={t('chooseDate')}
                                    placeholder={t('chooseDate')}
                                    name="date"
                                    availableDates={schedule?.map(
                                        (map) => map.date
                                    )}
                                    setOtherFieldValues={() => {
                                        setFieldValue('time', null);
                                        setFieldValue('people_num', null);
                                        setFieldValue('table', null);
                                    }}
                                />
                                <Select
                                    label={t('time')}
                                    title={t('chooseTime')}
                                    placeholder={t('chooseTime')}
                                    name="time"
                                    selectItems={
                                        schedule?.find(
                                            (item) => item.date === values.date
                                        ) && values?.date
                                            ? updateTimeForDate(
                                                  schedule,
                                                  values.date
                                              )
                                            : []
                                    }
                                    setOtherFieldValues={() => {
                                        setFieldValue('people_num', null);
                                        setFieldValue('table', null);
                                    }}
                                    disabled={
                                        !schedule?.find(
                                            (item) => item.date === values.date
                                        )
                                    }
                                />
                                <Select
                                    label={t('peopleNumber')}
                                    title={t('enterPeopleNumber')}
                                    placeholder={t('enterPeopleNumber')}
                                    name="people_num"
                                    selectItems={
                                        schedule?.find(
                                            (item) => item.date === values.date
                                        ) && values?.time
                                            ? updatePeopleForTime(
                                                  schedule,
                                                  values.date,
                                                  values.time
                                              )
                                            : []
                                    }
                                    setOtherFieldValues={() => {
                                        setFieldValue('table', null);
                                    }}
                                    disabled={
                                        !(
                                            schedule?.find(
                                                (item) =>
                                                    item.date === values.date
                                            ) && values?.time
                                        )
                                    }
                                />
                                <TableSelectForm
                                    name="table"
                                    label={t('table')}
                                    tableList={
                                        schedule?.find(
                                            (item) => item.date === values.date
                                        ) &&
                                        values?.time &&
                                        values?.people_num
                                            ? updateTableForPeople(
                                                  schedule,
                                                  values.date,
                                                  values.time,
                                                  values.people_num
                                              )
                                            : []
                                    }
                                    tableMapList={
                                        schedule?.find(
                                            (item) => item.date === values.date
                                        ) &&
                                        values?.time &&
                                        values?.people_num
                                            ? updateTablMapForPeople(
                                                  schedule,
                                                  values.date,
                                                  values.time,
                                                  values.people_num
                                              )
                                            : []
                                    }
                                />

                                <Input
                                    label={t('name')}
                                    placeholder={t('enterName')}
                                    name="name"
                                    maxLength={nameMaxLength}
                                    style={[
                                        FormStyle.input,
                                        { borderColor: theme.colors.primary },
                                    ]}
                                />
                                <Input
                                    label={t('email')}
                                    placeholder={t('enterEmail')}
                                    name="email"
                                    maxLength={descriptionMaxLength}
                                    style={[
                                        FormStyle.email,
                                        { borderColor: theme.colors.primary },
                                    ]}
                                />
                                <Input
                                    label={t('emailConfirmation')}
                                    placeholder={t('enterEmailConfirmation')}
                                    name="email_confirm"
                                    maxLength={descriptionMaxLength}
                                    style={[
                                        FormStyle.email,
                                        { borderColor: theme.colors.primary },
                                    ]}
                                />
                                <TelInput
                                    label={t('phoneNumber')}
                                    placeholder={t('enterPhoneNumber')}
                                    name="tel"
                                    style={{
                                        phoneArea: {
                                            ...FormStyle.phoneArea,
                                            container: {
                                                borderColor:
                                                    theme.colors.primary,
                                            },
                                        },
                                    }}
                                />
                                <CheckBoxItemDictForm
                                    label={t('options')}
                                    name="options"
                                    itemDict={reservationSetting}
                                />
                                <Input
                                    label={t('accommodation')}
                                    placeholder={t('enterAccommodation')}
                                    name="accommodation"
                                    maxLength={nameMaxLength}
                                    style={{
                                        ...FormStyle.input,
                                        borderColor: theme.colors.primary,
                                    }}
                                />
                                <Input
                                    label={t('notes')}
                                    placeholder={t('enterNotes')}
                                    name="notes"
                                    multiline={true}
                                    maxLength={descriptionMaxLength}
                                    style={[
                                        FormStyle.input,
                                        {
                                            height: 200,
                                            borderColor: theme.colors.primary,
                                        },
                                    ]}
                                />
                                {APP_STRIPE_PUBLIC_KEY && (
                                    <PaymentForm
                                        label={t('payment')}
                                        name="payment"
                                    />
                                )}
                                <Button
                                    title={t('next')}
                                    onPress={handleSubmit}
                                    disabled={!isValid}
                                />
                            </View>
                        );
                    }}
                </Formik>
            </View>
        </ScrollView>
    );
};

export default Reservation;
